import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Card, Badge, Button, Modal, Spinner } from 'react-bootstrap'
import { baseUrl } from 'api/helpers'
import { formatDateLocal } from 'helpers/datetime'

const ReceiptCard = ({ receiptScan, showDetails = true, scanType = 'receipt' }) => {
  const isInvoice = scanType === 'invoice'
  const [showImageModal, setShowImageModal] = useState(false)

  if (!receiptScan) return null

  // Construct full image URL by prepending API base URL
  const imageUrl = receiptScan.receipt_image_url
    ? `${baseUrl}${receiptScan.receipt_image_url}`
    : null

  // Check if the file is a PDF
  const isPdf = receiptScan.file_name?.toLowerCase().endsWith('.pdf')

  const pillStyle = (color) => ({
    fontSize: '0.7rem',
    backgroundColor: 'white',
    color,
    border: `1px solid #dee2e6`,
    borderRadius: '50rem'
  })

  const statusColors = {
    pending: '#6c757d',
    processing: '#38BEC9',
    matched: '#4767C5',
    manual_review: '#b8860b',
    failed: '#dc3545'
  }

  const getStatusBadge = (status) => {
    const color = statusColors[status] || '#6c757d'

    if (status === 'processing') {
      return (
        <Badge bg="" style={pillStyle(color)}>
          <Spinner animation="border" size="sm" className="me-1" style={{ width: '0.6rem', height: '0.6rem' }} />
          Processing
        </Badge>
      )
    }

    const textMap = {
      pending: 'Pending',
      matched: 'Matched',
      manual_review: 'Needs Review',
      failed: 'Failed'
    }

    return <Badge bg="" style={pillStyle(color)}>{textMap[status] || status}</Badge>
  }

  const getMatchTypeBadge = (matchType) => {
    if (!matchType) return null

    const text = matchType === 'manual' ? 'Manually Matched' : 'Matched'
    const config = { color: '#4767C5', text }
    return <Badge bg="" className="ms-2" style={pillStyle(config.color)}>{config.text}</Badge>
  }

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const getConfidencePercentage = (confidence) => {
    if (!confidence) return null
    return `${Math.round(confidence * 100)}%`
  }

  const handleDownload = () => {
    if (imageUrl) {
      window.open(imageUrl, '_blank')
    }
  }

  return (
    <>
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <strong>{isInvoice ? 'Invoice' : 'Receipt'}</strong>
            <span className="ms-2">{getStatusBadge(receiptScan.status)}</span>
            {receiptScan.match_type && getMatchTypeBadge(receiptScan.match_type)}
          </div>
          {receiptScan.match_confidence && (
            <Badge bg="" style={pillStyle('#38BEC9')}>
              Confidence: {getConfidencePercentage(receiptScan.match_confidence)}
            </Badge>
          )}
        </Card.Header>

        <Card.Body>
          <div className="row">
            {imageUrl && (
              <div className="col-md-4 mb-3 mb-md-0">
                {isPdf ? (
                  <div
                    className="d-flex flex-column align-items-center justify-content-center bg-light rounded"
                    style={{ height: '200px', cursor: 'pointer' }}
                    onClick={() => setShowImageModal(true)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" className="text-danger mb-2" viewBox="0 0 16 16">
                      <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z"/>
                      <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029zm1.379-1.901c-.166.076-.32.156-.459.238-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361.01.022.02.036.026.044a.266.266 0 0 0 .035-.012c.137-.056.355-.235.635-.572a8.18 8.18 0 0 0 .45-.606zm1.64-1.33a12.71 12.71 0 0 1 1.01-.193 11.744 11.744 0 0 1-.51-.858 20.801 20.801 0 0 1-.5 1.05zm2.446.45c.15.163.296.3.435.41.24.19.407.253.498.256a.107.107 0 0 0 .07-.015.307.307 0 0 0 .094-.125.436.436 0 0 0 .059-.2.095.095 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a3.876 3.876 0 0 0-.612-.053zM8.078 7.8a6.7 6.7 0 0 0 .2-.828c.031-.188.043-.343.038-.465a.613.613 0 0 0-.032-.198.517.517 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822.024.111.054.227.09.346z"/>
                    </svg>
                    <span className="text-muted small">PDF Document</span>
                    <span className="text-muted small">Click to view</span>
                  </div>
                ) : (
                  <img
                    src={imageUrl}
                    alt="Receipt"
                    className="img-fluid rounded cursor-pointer"
                    style={{ maxHeight: '200px', objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => setShowImageModal(true)}
                  />
                )}
              </div>
            )}

            <div className={imageUrl ? 'col-md-8' : 'col-12'}>
              {showDetails && receiptScan.status !== 'pending' && receiptScan.status !== 'processing' && (
                <div>
                  <h6 className="mb-3">Extracted Information</h6>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <small className="text-muted">Vendor</small>
                      <div>{receiptScan.effective_vendor_name || 'N/A'}</div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <small className="text-muted">Amount</small>
                      <div>{formatCurrency(receiptScan.effective_total_amount)}</div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <small className="text-muted">Date</small>
                      <div>{formatDateLocal(receiptScan.effective_transaction_date)}</div>
                    </div>
                    {isInvoice ? (
                      <>
                        {receiptScan.effective_invoice_number && (
                          <div className="col-md-6 mb-2">
                            <small className="text-muted">Invoice #</small>
                            <div>{receiptScan.effective_invoice_number}</div>
                          </div>
                        )}
                        {receiptScan.effective_po_number && (
                          <div className="col-md-6 mb-2">
                            <small className="text-muted">PO #</small>
                            <div>{receiptScan.effective_po_number}</div>
                          </div>
                        )}
                        {receiptScan.effective_due_date && (
                          <div className="col-md-6 mb-2">
                            <small className="text-muted">Due Date</small>
                            <div>{formatDateLocal(receiptScan.effective_due_date)}</div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="col-md-6 mb-2">
                          <small className="text-muted">Card Last 4</small>
                          <div>{receiptScan.effective_card_last_four || 'N/A'}</div>
                        </div>
                        {receiptScan.effective_invoice_number && (
                          <div className="col-md-6 mb-2">
                            <small className="text-muted">Invoice Number</small>
                            <div>{receiptScan.effective_invoice_number}</div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {receiptScan.status === 'processing' && (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary mb-2" role="status">
                    <span className="visually-hidden">Processing...</span>
                  </div>
                  <div>Processing {isInvoice ? 'invoice' : 'receipt'}...</div>
                </div>
              )}

              {receiptScan.status === 'failed' && (
                <div className="alert alert-danger mb-0">
                  <strong>Processing Failed</strong>
                  {receiptScan.error_message && (
                    <div className="small mt-1">{receiptScan.error_message}</div>
                  )}
                </div>
              )}

              {imageUrl && (
                <div className="mt-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleDownload}
                  >
                    {isInvoice ? 'View Full Invoice' : 'View Full Receipt'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card.Body>

        <Card.Footer className="text-muted small">
          Uploaded {formatDateLocal(receiptScan.created_at)}
        </Card.Footer>
      </Card>

      {/* Full Image Modal */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{isInvoice ? 'Invoice Image' : 'Receipt Image'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center" style={{ backgroundColor: '#f8f9fa' }}>
          {imageUrl && (
            isPdf ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="text-danger mb-3" viewBox="0 0 16 16">
                  <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z"/>
                  <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029zm1.379-1.901c-.166.076-.32.156-.459.238-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361.01.022.02.036.026.044a.266.266 0 0 0 .035-.012c.137-.056.355-.235.635-.572a8.18 8.18 0 0 0 .45-.606zm1.64-1.33a12.71 12.71 0 0 1 1.01-.193 11.744 11.744 0 0 1-.51-.858 20.801 20.801 0 0 1-.5 1.05zm2.446.45c.15.163.296.3.435.41.24.19.407.253.498.256a.107.107 0 0 0 .07-.015.307.307 0 0 0 .094-.125.436.436 0 0 0 .059-.2.095.095 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a3.876 3.876 0 0 0-.612-.053zM8.078 7.8a6.7 6.7 0 0 0 .2-.828c.031-.188.043-.343.038-.465a.613.613 0 0 0-.032-.198.517.517 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822.024.111.054.227.09.346z"/>
                </svg>
                <h5 className="mb-2">{isInvoice ? 'PDF Invoice' : 'PDF Receipt'}</h5>
                <p className="text-muted">Click the button below to view this PDF</p>
              </div>
            ) : (
              <img
                src={imageUrl}
                alt="Receipt"
                className="img-fluid"
                style={{ maxHeight: '80vh' }}
              />
            )
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light-primary" onClick={() => setShowImageModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleDownload}>
            {isPdf ? 'View PDF' : 'Download'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

ReceiptCard.propTypes = {
  receiptScan: PropTypes.object,
  showDetails: PropTypes.bool,
  scanType: PropTypes.string
}

export default ReceiptCard
